import { useEffect, useState } from 'react';
import {
  acquireGraphToken,
  connectMicrosoftAccount,
  disconnectMicrosoftAccount,
  getActiveAccount,
  getMsalApp,
} from '../../lib/graph/msalClient';
import {
  getTokenStatus,
  subscribeTokenStatus,
  type TokenStatus,
} from '../../lib/graph/tokenStatus';
import { VaultOutboundStatus } from './VaultOutboundStatus';
import { testIds } from '../../lib/testIds';

const FONT = "var(--xp-font-body)";

const groupBoxStyle: React.CSSProperties = {
  border: '1px solid #808080',
  padding: '12px 10px 8px',
  marginBottom: 12,
  position: 'relative',
};

const groupBoxLabelStyle: React.CSSProperties = {
  position: 'absolute',
  top: -8,
  left: 8,
  background: '#ece9d8',
  padding: '0 4px',
  fontSize: 11,
  fontWeight: 'bold',
};

function TokenStatusBadge({
  status,
  accountEmail,
}: {
  status: TokenStatus;
  accountEmail: string;
}) {
  let dotColor = '#888';
  let label = '';
  switch (status) {
    case 'valid':
      dotColor = '#0a0';
      label = `Microsoft: connected as ${accountEmail}`;
      break;
    case 'expiring-soon':
      dotColor = '#c80';
      label = 'Microsoft: reconnecting in background…';
      break;
    case 'expired':
      dotColor = '#c00';
      label = 'Microsoft: needs reconnect';
      break;
    case 'unknown':
      dotColor = '#888';
      label = 'Microsoft: checking…';
      break;
  }
  return (
    <div
      data-testid={testIds.microsoftAccount.tokenStatusBadge}
      data-token-status={status}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: '#000',
        marginBottom: 8,
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: 4,
          background: dotColor,
        }}
      />
      {label}
    </div>
  );
}

function Win95Button({ label, onClick, disabled, primary }: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        minWidth: 90,
        height: 26,
        padding: '0 12px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: primary ? 'bold' : 'normal',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#808080' : '#000',
      }}
    >
      {label}
    </button>
  );
}

/**
 * Settings panel for linking a Microsoft account to Repancy. Lives in the
 * Settings window or anywhere else a host wants to mount it. State is owned
 * by the MSAL singleton, so refreshing this panel always reflects truth.
 */
export function MicrosoftAccountPanel() {
  const [accountName, setAccountName] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'disconnecting' | 'reconnecting'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(
    () => getTokenStatus().status,
  );
  const [refreshToast, setRefreshToast] = useState<string | null>(null);
  const configured = Boolean(import.meta.env.VITE_MSAL_CLIENT_ID);

  async function refresh() {
    try {
      await getMsalApp(); // ensures MSAL is initialized so getActiveAccount is meaningful
      const acc = getActiveAccount();
      setAccountName(acc?.username ?? null);
      if (acc !== null) {
        // Probe MSAL silently so the token-status store gets a fresh
        // expiresOn — fires markTokenValid inside acquireGraphToken.
        try {
          await acquireGraphToken();
        } catch {
          // Already reflected via markTokenExpired in acquireGraphToken's
          // InteractionRequiredAuthError branch; ignore here.
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (configured) {
      void refresh();
    }
  }, [configured]);

  useEffect(() => {
    const unsub = subscribeTokenStatus((next) => {
      const prevStatus = tokenStatus;
      setTokenStatus(next.status);
      if (prevStatus === 'expired' && next.status === 'valid') {
        setRefreshToast('Reconnected to Microsoft');
        setTimeout(() => setRefreshToast(null), 4000);
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConnect() {
    setStatus('connecting');
    setError(null);
    try {
      await connectMicrosoftAccount();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStatus('idle');
    }
  }

  async function handleDisconnect() {
    setStatus('disconnecting');
    setError(null);
    try {
      await disconnectMicrosoftAccount();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStatus('idle');
    }
  }

  async function handleReconnect() {
    setStatus('reconnecting');
    setError(null);
    try {
      // Fresh interactive sign-in via MSAL popup. Will re-fire
      // markTokenValid through acquireGraphToken's success path.
      await connectMicrosoftAccount();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStatus('idle');
    }
  }

  return (
    <div style={groupBoxStyle} data-testid="ms-account-panel">
      <span style={groupBoxLabelStyle}>Microsoft 365</span>

      {!configured && (
        <div style={{ fontSize: 11, color: '#800', marginBottom: 8 }}>
          VITE_MSAL_CLIENT_ID is not set. See .env.example to register an Azure SPA app.
        </div>
      )}

      <div style={{ fontSize: 12, marginBottom: 8 }}>
        Linked account:{' '}
        <strong>{accountName ?? '(none)'}</strong>
      </div>

      {accountName !== null && (
        <TokenStatusBadge
          status={tokenStatus}
          accountEmail={accountName}
        />
      )}

      {/* E.4 — Vault outbound status surface (additive; hides outside
          portal scope, so this is a no-op for non-portal SettingsWindow
          mounts). */}
      <VaultOutboundStatus />

      {tokenStatus === 'expired' && accountName !== null && (
        <div style={{ marginBottom: 8 }}>
          <Win95Button
            label={status === 'reconnecting' ? 'Reconnecting…' : 'Reconnect Microsoft'}
            onClick={handleReconnect}
            disabled={!configured || status !== 'idle'}
            primary
          />
        </div>
      )}

      {refreshToast !== null && (
        <div
          data-testid={testIds.microsoftAccount.refreshSuccessToast}
          style={{
            fontSize: 11,
            color: '#060',
            marginBottom: 8,
            padding: '4px 6px',
            border: '1px solid #060',
            background: '#dfd',
          }}
        >
          {refreshToast}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        {accountName ? (
          <Win95Button
            label={status === 'disconnecting' ? 'Disconnecting…' : 'Disconnect'}
            onClick={handleDisconnect}
            disabled={!configured || status !== 'idle'}
          />
        ) : (
          <Win95Button
            label={status === 'connecting' ? 'Connecting…' : 'Connect Microsoft account'}
            onClick={handleConnect}
            disabled={!configured || status !== 'idle'}
            primary
          />
        )}
      </div>

      {error && (
        <div style={{ fontSize: 11, color: '#800', marginTop: 8 }}>
          {error}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#444', marginTop: 8, lineHeight: 1.5 }}>
        Connecting lets you import documents from SharePoint and save edits back
        to the same files. Repancy stores only a link to each file; the content
        lives in your tenant.
      </div>
    </div>
  );
}
